// components/common/dropdowns/TaskTypeDropdown.tsx
import React, { useMemo } from 'react';
import { Check, ChevronDownIcon } from 'lucide-react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { WorkType, WorkTypeCategory } from 'types/project/projectDefaults';

// ---------------------------------------------------------------------------
// Inline SVG icon components — zero HTTP requests, renders instantly at scale.
// Previously these were <img src="/assets/..."> tags which caused 300+ parallel
// browser image requests when loading large work-item lists, causing some icons
// to silently fail or not render.
// ---------------------------------------------------------------------------

const EpicIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Epic">
    <path
      d="M2 2V3M2 3L3.84667 2.538C5.23627 2.1907 6.70427 2.35197 7.98533 2.99267L8.05733 3.02867C9.31252 3.6562 10.748 3.82393 12.114 3.50267L14.1873 3.01467C13.9353 5.34103 13.9364 7.68788 14.1907 10.014L12.1147 10.502C10.7485 10.8236 9.31279 10.6561 8.05733 10.0287L7.98533 9.99267C6.70427 9.35197 5.23627 9.1907 3.84667 9.538L2 10M2 3V10M2 14V10"
      stroke="#818CF8"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StoryIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Story">
    <path
      d="M14.341 3.579C13.994 3.106 13.51 2.552 12.979 2.021C12.448 1.49 11.894 1.006 11.421 0.659C10.615 0.068 10.224 0 10 0H2.25C1.561 0 1 0.561 1 1.25V14.75C1 15.439 1.561 16 2.25 16H13.75C14.439 16 15 15.439 15 14.75V5C15 4.776 14.932 4.385 14.341 3.579ZM12.271 2.729C12.751 3.209 13.127 3.641 13.405 4H10.999V1.595C11.358 1.873 11.792 2.249 12.271 2.729ZM14 14.75C14 14.886 13.886 15 13.75 15H2.25C2.18394 14.9992 2.1208 14.9726 2.07408 14.9259C2.02737 14.8792 2.00078 14.8161 2 14.75V1.25C2 1.115 2.115 1 2.25 1H10V4.5C10 4.63261 10.0527 4.75979 10.1464 4.85355C10.2402 4.94732 10.3674 5 10.5 5H14V14.75Z"
      fill="#2DD4BF"
    />
    <path
      d="M11.5 13H4.5C4.36739 13 4.24021 12.9473 4.14645 12.8536C4.05268 12.7598 4 12.6326 4 12.5C4 12.3674 4.05268 12.2402 4.14645 12.1464C4.24021 12.0527 4.36739 12 4.5 12H11.5C11.6326 12 11.7598 12.0527 11.8536 12.1464C11.9473 12.2402 12 12.3674 12 12.5C12 12.6326 11.9473 12.7598 11.8536 12.8536C11.7598 12.9473 11.6326 13 11.5 13ZM11.5 11H4.5C4.36739 11 4.24021 10.9473 4.14645 10.8536C4.05268 10.7598 4 10.6326 4 10.5C4 10.3674 4.05268 10.2402 4.14645 10.1464C4.24021 10.0527 4.36739 10 4.5 10H11.5C11.6326 10 11.7598 10.0527 11.8536 10.1464C11.9473 10.2402 12 10.3674 12 10.5C12 10.6326 11.9473 10.7598 11.8536 10.8536C11.7598 10.9473 11.6326 11 11.5 11ZM11.5 9H4.5C4.36739 9 4.24021 8.94732 4.14645 8.85355C4.05268 8.75979 4 8.63261 4 8.5C4 8.36739 4.05268 8.24021 4.14645 8.14645C4.24021 8.05268 4.36739 8 4.5 8H11.5C11.6326 8 11.7598 8.05268 11.8536 8.14645C11.9473 8.24021 12 8.36739 12 8.5C12 8.63261 11.9473 8.75979 11.8536 8.85355C11.7598 8.94732 11.6326 9 11.5 9Z"
      fill="#2DD4BF"
    />
  </svg>
);

const TaskIcon = () => (
  <svg width="12" height="15" viewBox="0 0 12 15" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Task">
    <path
      d="M4.5 0.75H1.5C1.086 0.75 0.75 1.086 0.75 1.5V13C0.75 13.414 1.086 13.75 1.5 13.75H10C10.414 13.75 10.75 13.414 10.75 13V7M4.5 0.75H4.75C6.3413 0.75 7.86742 1.38214 8.99264 2.50736C10.1179 3.63258 10.75 5.1587 10.75 6.75V7M4.5 0.75C5.09674 0.75 5.66903 0.987053 6.09099 1.40901C6.51295 1.83097 6.75 2.40326 6.75 3V4C6.75 4.414 7.086 4.75 7.5 4.75H8.5C9.09674 4.75 9.66903 4.98705 10.091 5.40901C10.5129 5.83097 10.75 6.40326 10.75 7M3.75 9.25L5.25 10.75L7.75 7.25"
      stroke="#FBBF24"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SubTaskIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Subtask">
    <path
      d="M3.99935 6.00065H7.99935M2.66602 3.33398H5.33268M3.99935 3.33398V10.6673C3.99935 10.8441 4.06959 11.0137 4.19461 11.1387C4.31964 11.2637 4.4892 11.334 4.66602 11.334H7.99935M7.99935 5.33398C7.99935 5.15717 8.06959 4.9876 8.19461 4.86258C8.31964 4.73756 8.4892 4.66732 8.66602 4.66732H12.666C12.8428 4.66732 13.0124 4.73756 13.1374 4.86258C13.2624 4.9876 13.3327 5.15717 13.3327 5.33398V6.66732C13.3327 6.84413 13.2624 7.0137 13.1374 7.13872C13.0124 7.26375 12.8428 7.33398 12.666 7.33398H8.66602C8.4892 7.33398 8.31964 7.26375 8.19461 7.13872C8.06959 7.0137 7.99935 6.84413 7.99935 6.66732V5.33398ZM7.99935 10.6673C7.99935 10.4905 8.06959 10.3209 8.19461 10.1959C8.31964 10.0709 8.4892 10.0007 8.66602 10.0007H12.666C12.8428 10.0007 13.0124 10.0709 13.1374 10.1959C13.2624 10.3209 13.3327 10.4905 13.3327 10.6673V12.0007C13.3327 12.1775 13.2624 12.347 13.1374 12.4721C13.0124 12.5971 12.8428 12.6673 12.666 12.6673H8.66602C8.4892 12.6673 8.31964 12.5971 8.19461 12.4721C8.06959 12.347 7.99935 12.1775 7.99935 12.0007V10.6673Z"
      stroke="#06B6D4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BugIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Bug">
    <path
      d="M7.99934 8.50067C8.76468 8.50067 9.51801 8.554 10.2547 8.65867C10.946 8.756 11.4987 9.30267 11.4987 10.0007C11.4987 12.486 9.93201 14.5007 7.99868 14.5007C6.06534 14.5007 4.49934 12.486 4.49934 10.0007C4.49934 9.30333 5.05268 8.756 5.74334 8.65867C6.49072 8.5533 7.24457 8.50051 7.99934 8.50067ZM7.99934 8.50067C9.92134 8.50067 11.764 8.83933 13.4707 9.46067C13.3902 10.8348 13.132 12.1928 12.7027 13.5007M7.99934 8.50067C6.07734 8.50067 4.23468 8.83933 2.52734 9.46067C2.61068 10.8633 2.87401 12.218 3.29601 13.5007M7.99934 8.50067C8.20219 8.5007 8.40293 8.4596 8.58943 8.37984C8.77594 8.30008 8.94433 8.18332 9.08442 8.03662C9.22451 7.88992 9.33339 7.71634 9.40448 7.52636C9.47557 7.33638 9.50739 7.13396 9.49801 6.93133M7.99934 8.50067C7.7965 8.5007 7.59576 8.4596 7.40925 8.37984C7.22275 8.30008 7.05436 8.18332 6.91427 8.03662C6.77418 7.88992 6.6653 7.71634 6.59421 7.52636C6.52312 7.33638 6.4913 7.13396 6.50068 6.93133M9.49801 6.93133C9.48018 6.54588 9.31448 6.18212 9.03537 5.91568C8.75626 5.64923 8.38521 5.5006 7.99934 5.50067M9.49801 6.93133C10.682 6.82166 11.8506 6.57971 12.9807 6.21C12.944 5.45867 12.856 4.72067 12.7193 4.00067M6.50068 6.93133C6.51851 6.54588 6.68421 6.18212 6.96331 5.91568C7.24242 5.64923 7.61348 5.5006 7.99934 5.50067M6.50068 6.93133C5.29601 6.81933 4.13068 6.574 3.01868 6.21C3.05452 5.46839 3.14185 4.73017 3.28001 4.00067M7.99934 5.50067C8.66268 5.50067 9.31334 5.44733 9.94734 5.34333C10.216 5.29933 10.4407 5.10467 10.4773 4.83533C10.5456 4.31986 10.4527 3.79589 10.2113 3.33533M7.99934 5.50067C7.33601 5.50067 6.68601 5.44733 6.05134 5.34333C5.78334 5.29933 5.55801 5.10467 5.52134 4.83533C5.45086 4.3191 5.54412 3.79376 5.78801 3.33333M5.78801 3.33333C5.50405 3.14423 5.2453 2.92043 5.01801 2.666C5.06468 2.246 5.19801 1.85133 5.40068 1.50133M5.78801 3.33333C6.00024 2.93063 6.31839 2.59418 6.70815 2.35901C7.0979 2.12385 7.54447 1.99956 7.99968 1.99956C8.45488 1.99956 8.90145 2.12385 9.29121 2.35901C9.68096 2.59418 9.99912 2.93129 10.2113 3.334C10.496 3.14533 10.7547 2.92067 10.9813 2.668C10.9361 2.25626 10.8056 1.85847 10.598 1.5"
      stroke="#EF4444"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const workTypeUIMap: Record<
  WorkTypeCategory,
  { icon: React.ReactNode; color: string }
> = {
  EPIC:     { icon: <EpicIcon />,    color: 'text-[#818CF8]' },
  STORY:    { icon: <StoryIcon />,   color: 'text-[#2DD4BF]' },
  TASK:     { icon: <TaskIcon />,    color: 'text-[#FBBF24]' },
  SUB_TASK: { icon: <SubTaskIcon />, color: 'text-[#06B6D4]' },
  BUG:      { icon: <BugIcon />,     color: 'text-[#EF4444]' },
};

/** One line on what each type is for, shown beside it when the menu has `showDetails`. */
const workTypeDescriptions: Record<WorkTypeCategory, string> = {
  EPIC: 'Groups work across sprints',
  STORY: 'User-facing work',
  TASK: 'A piece of work',
  SUB_TASK: 'Part of a larger item',
  BUG: 'A defect',
};
const sizeClasses = {
  sm: 'px-2 py-1 text-xs',
  md: 'px-3 py-1 text-xs',
  lg: 'px-4 py-2 text-sm',
};

const iconSizes = {
  sm: 'text-sm',
  md: 'text-lg',
  lg: 'text-xl',
};

interface TaskTypeDropdownProps {
  workTypes: WorkType[];
  currentTypeUuid: string;
  onTypeChange: (workType: WorkType) => void;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  /** Optional mapping of WorkType UUIDs to a reason why they are disabled */
  disabledOptions?: Record<string, string>;
  /** Shows each type's description and a check on the current one. */
  showDetails?: boolean;
  /** A note under the options, e.g. why a type isn't offered. */
  footerNote?: string;
}


const TaskTypeDropdown: React.FC<TaskTypeDropdownProps> = ({
  workTypes,
  currentTypeUuid,
  onTypeChange,
  disabled = false,
  size = 'md',
  disabledOptions = {},
  showDetails = false,
  footerNote,
}) => {
  const activeType = useMemo(
    () => workTypes.find((t) => t.workTypeUuid === currentTypeUuid),
    [workTypes, currentTypeUuid]
  );

  if (!activeType) return null;

  const activeUI = workTypeUIMap[activeType.workTypeCategory];

  return (
    <Menu as="div" className="relative inline-block text-left">
      <MenuButton
        type="button"
        disabled={disabled || workTypes.length <= 1}
        className={`${sizeClasses[size]} rounded-lg font-medium flex items-center gap-2 bg-transparent text-neutral hover:bg-primary-subtle transition-colors disabled:opacity-50 focus:outline-none focus:ring-0`}
      >
        <span className={`${activeUI.color} ${iconSizes[size]}`}>
          {activeUI.icon}
        </span>
        {workTypes.length > 1 && (
          <ChevronDownIcon className="text-base text-neutral-medium" />
        )}
      </MenuButton>

      <MenuItems
        transition
        className={`absolute left-0 mt-2 z-[9999] ${showDetails ? 'w-[300px]' : 'min-w-[160px]'} rounded-lg bg-primary-light border border-primary-variant shadow-lg focus:outline-none focus:ring-0`}
      >
        <div className="py-1">
          {workTypes.map((type, index) => {
            const ui = workTypeUIMap[type.workTypeCategory];
            const isDisabled = !!disabledOptions[type.workTypeUuid];
            const disabledReason = disabledOptions[type.workTypeUuid];

            return (
              <MenuItem key={type.workTypeUuid} disabled={isDisabled}>
                {({ focus, disabled: isItemDisabled }) => (
                  <div className="relative group/item">
                    <button
                      onClick={() => !isItemDisabled && onTypeChange(type)}
                      disabled={isItemDisabled}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors
                        ${isItemDisabled ? 'opacity-50 cursor-not-allowed' : focus ? 'bg-primary-subtle' : 'hover:bg-primary-subtle'}
                        ${index === 0 ? 'rounded-t-lg' : ''}`}
                    >
                      <span className={`${ui.color} text-lg`}>{ui.icon}</span>
                      <span className="text-neutral">
                        {type.name}
                      </span>
                      {showDetails && (
                        <span className="ml-auto flex items-center gap-2 text-xs text-neutral-medium">
                          {workTypeDescriptions[type.workTypeCategory]}
                          {/* Always takes its space, so the descriptions line up. */}
                          <Check
                            size={14}
                            className={type.workTypeUuid === currentTypeUuid ? 'text-[#A78BFA]' : 'invisible'}
                          />
                        </span>
                      )}
                    </button>
                    
                    {isItemDisabled && disabledReason && (
                      <div className="absolute left-full top-1/2 -translate-y-1/2 ml-3 w-52 p-3 bg-primary-variant/95 backdrop-blur-md border border-primary-subtle rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.5)] text-[11px] leading-relaxed text-neutral-medium opacity-0 group-hover/item:opacity-100 group-hover/item:translate-x-1 pointer-events-none transition-all duration-200 z-[10000] scale-95 group-hover/item:scale-100">
                        <div className="absolute left-[-4px] top-1/2 -translate-y-1/2 w-2 h-2 bg-primary-variant/95 border-l border-b border-primary-subtle rotate-45" />
                        <div className="flex flex-col gap-1">
                          <span className="font-semibold text-amber-500/90 flex items-center gap-1.5">
                            <span className="w-1 h-1 rounded-full bg-amber-500" />
                            Type Not Available
                          </span>
                          <span className="text-neutral/80 italic">
                            This type can’t be selected because some child items aren’t supported under it.
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </MenuItem>
            );
          })}
        </div>
        {footerNote && (
          <p className="mx-3 border-t border-primary-subtle py-2.5 text-xs text-neutral-medium">{footerNote}</p>
        )}
      </MenuItems>
    </Menu>
  );
};

export default TaskTypeDropdown;
